import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  Suspense,
} from "react";
import { Container, Row, Col, Modal, Button, Card } from "react-bootstrap";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import HeadingBanner from "../components/HeadingBanner";
import {
  whoami,
  getClassroomDashboardClasses,
  getTeachers,
  getSchools,
  getCountryGrades,
  shareClass,
  getTrialistsBySchool,
  getAvailableTransitionedClasses,
  removeOrReplaceClassOwner,
  editClass,
  checkTeacherHasClaimedClass,
  getStudents,
  getClass,
  getCountryId,
  getScreeningWindowStatuses,
  getPilotBySchoolId,
  getPurchasesBySchool,
  getMessageByMessageId,
} from "../services/APIClient";
import "../components/ClassCard.css";
import "../components/handover/ClassroomHandoverShell.css";
import "../components/handover/ClassroomSetupEntry.css";
import useAcademicYearOptions from "../utils/useAcademicYearOptions";
import useClassroomHandover from "../hooks/useClassroomHandover";
import useHandoverClassrooms from "../hooks/useHandoverClassrooms";
import {
  destinationWithSelection,
  filterClaimablesBySelection,
  getClassroomHandoverDashboardGate,
  parseClassroomHandoverSelection,
} from "../utils/classroomHandoverSelection";

// Lazy Loaded Components
const ClassCard = React.lazy(() => import("../components/ClassCard"));
const CreateClassCard = React.lazy(() =>
  import("../components/CreateClassCard")
);
const CreateTrialClassCard = React.lazy(() =>
  import("../components/CreateTrialClassCard")
);
const CreateSETClassCard = React.lazy(() =>
  import("../components/CreateSETClassCard")
);
const LeftPanel = React.lazy(() => import("../components/LeftPanel"));
const ClassFilter = React.lazy(() => import("../components/ClassFilter"));
const FreeTrialBanner = React.lazy(() =>
  import("../components/FreeTrialBanner")
);
const OrientationPrompt = React.lazy(() =>
  import("../components/OrientationPrompt")
);
const StudentVerificationModal = React.lazy(() =>
  import("../components/StudentVerificationModal")
);
const ClassroomSetupEntry = React.lazy(() =>
  import("../components/handover/ClassroomSetupEntry")
);
const DifferentGroupingsPlaceholder = React.lazy(() =>
  import("./DifferentGroupingsPlaceholder")
);
const DifferentGroupingsNomination = React.lazy(() =>
  import("./DifferentGroupingsNomination")
);
const HandoverMyClasses = React.lazy(() =>
  import("../components/handover/HandoverMyClasses")
);

function ClassAdminDashboard() {
  const [currentAcademicYear] = useAcademicYearOptions({ previousYears: 0 });
  const location = useLocation();
  const navigate = useNavigate();
  const isDifferentGroupingsRoute = location.pathname === "/classroom-handover/different-groupings";
  const isDifferentGroupingsNominationRoute = location.pathname === "/classroom-handover/different-groupings/nominate";
  const isDifferentGroupingsJourneyRoute = isDifferentGroupingsRoute || isDifferentGroupingsNominationRoute;
  const differentGroupingsFallback = useMemo(() => {
    const academicYear = new URLSearchParams(location.search).get("academicYear");
    return academicYear
      ? `/classadmindashboard?academicYear=${encodeURIComponent(academicYear)}`
      : "/classadmindashboard";
  }, [location.search]);
  const [schoolid, setSchoolid] = useState(null);
  const [teacherid, setTeacherid] = useState(null);
  const [name, setName] = useState("");
  const [teachers, setTeachers] = useState(null);
  const [rollNumber, setRollNumber] = useState(null);
  const [teacherEmail, setTeacherEmail] = useState(null);
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
  const normalProgressionPoint = useMemo(() => {
    const grades = gradeLevelOptions[0];
    const label = grades?.grade1;
    return typeof label === "string" && label.trim()
      ? { key: "grade1", label, enabled: true, destination: { url: "/classadmindashboard", academicYear: currentAcademicYear.storage, progressionKey: "grade1" } }
      : null;
  }, [currentAcademicYear.storage, gradeLevelOptions]);
  const [filteredClassObjects, setFilteredClassObjects] = useState([]);
  const [mostRecentClassId, setMostRecentClassId] = useState(null);
  const [teacherRoles, setTeacherRoles] = useState(null);
  const [countryId, setCountryId] = useState(null);
  const [trialistDetails, setTrialistDetails] = useState(null);
  const [freeDaysLeft, setFreeDaysLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTrialEndedModal, setShowTrialEndedModal] = useState(false);
  const classroomHandover = useClassroomHandover({
    schoolId: schoolid,
    academicYear: currentAcademicYear.storage,
    refreshKey: location.key,
  });
  const effectiveDifferentGroupingsTask = classroomHandover.differentGroupingsTask;
  const requestedClassroomHandoverSelection = useMemo(
    () => parseClassroomHandoverSelection(location.search),
    [location.search]
  );
  const legacyClassroomViewRequested = useMemo(
    () => new URLSearchParams(location.search).get("view") === "classes",
    [location.search]
  );
  const {
    legacySelection: legacyClassroomSelection,
    validatedSelection: validatedClassroomHandoverSelection,
    showEntry: isClassroomSetupEntryVisible,
    showReadAlert: isClassroomHandoverReadFailure,
    useHandoverShell: isClassroomHandoverJourney,
    loadLegacyClaimables: legacyClaimablesAllowed,
  } = useMemo(
    () => getClassroomHandoverDashboardGate({
      entry: classroomHandover.entry,
      entryStatus: classroomHandover.status,
      requestedSelection: requestedClassroomHandoverSelection,
      legacyProgressionKey: normalProgressionPoint?.key,
      legacyViewRequested: legacyClassroomViewRequested,
      differentGroupingsTask: effectiveDifferentGroupingsTask,
      differentGroupingsTaskStatus: classroomHandover.differentGroupingsTaskStatus,
    }),
    [
      classroomHandover.entry,
      classroomHandover.status,
      classroomHandover.differentGroupingsTaskStatus,
      effectiveDifferentGroupingsTask,
      legacyClassroomViewRequested,
      normalProgressionPoint?.key,
      requestedClassroomHandoverSelection,
    ]
  );
  const progressedClassrooms = useHandoverClassrooms({
    schoolId: schoolid,
    selection: validatedClassroomHandoverSelection,
  });

  // State for class selection modal
  const [showClassSelectionModal, setShowClassSelectionModal] = useState(false);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [modalScreen, setModalScreen] = useState('selection');
  const [selectedClassName, setSelectedClassName] = useState('');
  const [isUpdatingClassName, setIsUpdatingClassName] = useState(false);
  const [classNameUpdateSuccess, setClassNameUpdateSuccess] = useState(false);
  const [selectedClassGradeLevel, setSelectedClassGradeLevel] = useState(null);

  // Teacher restrictions state
  const [isSeniorInfantTeacher, setIsSeniorInfantTeacher] = useState(false);
  const [isFirstClassTeacher, setIsFirstClassTeacher] = useState(false);
  const [hasClaimedClass, setHasClaimedClass] = useState(false);
  const [seniorInfantCheckLoading, setSeniorInfantCheckLoading] = useState(false);

  const [classesNeedingClaiming, setClassesNeedingClaiming] = useState([]);
  const [claimableClasses, setClaimableClasses] = useState([]);
  const [claimingId, setClaimingId] = useState(null);

  // Student verification modal state
  const [showStudentVerificationModal, setShowStudentVerificationModal] = useState(false);
  const [selectedClassForVerification, setSelectedClassForVerification] = useState(null);
  const [isNavigatingAfterVerification, setIsNavigatingAfterVerification] = useState(false);

  // State for selected grade from filter for banner updates
  const [selectedGradeForBanner, setSelectedGradeForBanner] = useState(null);

  const [existingConversionMethods, setExistingConversionMethods] = useState([]);
    const [pilotData, setPilotData] = useState(null);
    const [screeningWindowData, setScreeningWindowData] = useState(null);
    const DEFAULT_COUNTRY_ID = '07d45b79-24b1-435f-b82a-93861b23a86e';
    const isPilot = pilotData && pilotData.length > 0 && pilotData[0].active;
    const [latestPurchase, setLatestPurchase] = useState(null);
  const [convertedMessage, setConvertedMessage] = useState(null);
  const [convertedMessageG1, setConvertedMessageG1] = useState(null);
  const [convertedMessageG3, setConvertedMessageG3] = useState(null);
  const [claimBannerMessage, setClaimBannerMessage] = useState(null);
  

  const { expired = false, purchased = false, purchased_2425 = false, purchased_2526 = false } = trialistDetails || {};


  const now = new Date();

  const nextPaymentDate = latestPurchase?.next_payment_date
    ? new Date(latestPurchase.next_payment_date)
    : null;

  const hasValidPurchaseDate =
    nextPaymentDate &&
    !Number.isNaN(nextPaymentDate.getTime()) &&
    now <= nextPaymentDate;

  const isAccessRestricted = expired && !hasValidPurchaseDate && !isPilot;

  const teacherName = name;

  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );

  // Higher-tier override roles
  const higherTieredAccess = ["senco", "set", "special educator/set", "principal", "secretary"];

  // Does the current teacher have any higher-tier role?
  const hasHigherTieredAccess = useMemo(() => {
    const roles = (teacherRoles ?? []).map((r) => String(r).toLowerCase());
    return roles.some((r) => higherTieredAccess.some((h) => r.includes(h)));
  }, [teacherRoles]);

  // Single source of truth for whether restrictions should apply
  const restrictionApplies = (isSeniorInfantTeacher || isFirstClassTeacher) && !hasHigherTieredAccess;

  // Dynamic banner message function
  const getBannerMessage = () => {
    const baseStyle = {
      backgroundColor: '#cc5182ff',
      border: '2px solid #f43782ff',
      borderRadius: '12px',
      padding: '20px',
      textAlign: 'center',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      fontSize: '150%',
      fontWeight: '600',
      color: '#ffffffff',
      margin: '0 auto',
      maxWidth: '100%'
    };

    let message = convertedMessage?.text || ""

    // Customize based on selected grade
    if (selectedGradeForBanner === 'grade1') {
      message = convertedMessageG1?.text || ""
    } else if (selectedGradeForBanner === 'grade2') {
      message = convertedMessage?.text || ""
    } else if (selectedGradeForBanner === 'grade3') {
      message = convertedMessageG3?.text || ""
    }

    if (typeof message === 'string') {
      message = message.replace(/\r?\n/g, '<br/>');
    }

    return { style: baseStyle, message };
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        const response = await whoami();
        const { schoolid, id, name, additionalRoles, email } =
          response.data.stakeholder;

        setSchoolid(schoolid);
        setTeacherid(id);
        setName(name);
        setTeacherEmail(email);
        setTeacherRoles(additionalRoles);
      } catch (error) {
        console.error("Error during initialization:", error);
      }
    };

    initialize();
  }, []);

  // Check teacher restrictions for both Senior Infant and 1st Class teachers
  useEffect(() => {
    const checkTeacherRestrictions = async () => {
      if (schoolid && teacherid && teacherRoles) {
        setSeniorInfantCheckLoading(true);

        try {
          // Check if user is a Senior Infant teacher (case insensitive)
          const isSeniorInfant = teacherRoles.some(role =>
            role.toLowerCase().includes('senior infant')
          );

          // Check if user is a 1st Class teacher (case insensitive)
          const isFirstClass = teacherRoles.some(role =>
            role.toLowerCase().includes('1st class')
          );

          setIsSeniorInfantTeacher(isSeniorInfant);
          setIsFirstClassTeacher(isFirstClass);

          // Override: higher-tier roles remove restrictions entirely
          if ((isSeniorInfant || isFirstClass) && hasHigherTieredAccess) {
            setHasClaimedClass(false);
            return;
          }

          if (isSeniorInfant || isFirstClass) {
            // For mixed roles, check if they've claimed a class for either grade level
            const gradeLevelsToCheck = [];
            if (isSeniorInfant) gradeLevelsToCheck.push(2);
            if (isFirstClass) gradeLevelsToCheck.push(3);

            try {
              // Check if teacher has claimed a class for any of their applicable grade levels
              const claimChecks = await Promise.all(
                gradeLevelsToCheck.map(gradeLevel =>
                  checkTeacherHasClaimedClass(schoolid, teacherid, currentAcademicYear.storage, gradeLevel)
                )
              );

              // If they've claimed a class for any grade level, set hasClaimedClass to true
              const hasClaimedAny = claimChecks.some(check => check.hasClaimedClass);
              setHasClaimedClass(hasClaimedAny);
            } catch (error) {
              console.error("Error calling checkTeacherHasClaimedClass:", error);
              setHasClaimedClass(false);
            }
          } else {
            setHasClaimedClass(false);
          }
        } catch (error) {
          console.error("Error in checkTeacherRestrictions:", error);
        } finally {
          setSeniorInfantCheckLoading(false);
        }
      }
    };

    checkTeacherRestrictions();
  }, [schoolid, teacherid, teacherRoles, hasHigherTieredAccess, currentAcademicYear]);

useEffect(() => {
  const fetchSchoolData = async () => {
    try {
      const [teachersResponse, schoolDataResponse] = await Promise.all([
        getTeachers(schoolid),
        getCountryId(schoolid), // Now returns both countryId and rollnumber
      ]);

      setTeachers(teachersResponse);

      const classesResponse = await getClassroomDashboardClasses(schoolid).catch(error => {
        console.error("Error fetching classroom classes:", error);
        return [];
      });
      setClasses(classesResponse);

      const uniqueClasses = Array.from(
        new Set(classesResponse.map((cls) => cls.id))
      ).map((id) => {
        return classesResponse.find((cls) => cls.id === id);
      });

      const filtered = uniqueClasses.filter((cls) => {
        const createdYear = new Date(cls.date_created).getFullYear();
        const updatedYear = new Date(cls.date_updated).getFullYear();
        return createdYear === 2023 && updatedYear === 2024;
      });

      setFilteredClassObjects(filtered);

      const sortedClasses = uniqueClasses.sort((a, b) => {
        const aDateCreated = new Date(a.date_created);
        const bDateCreated = new Date(b.date_created);

        if (aDateCreated > bDateCreated) return -1;
        if (aDateCreated < bDateCreated) return 1;

        const aNeedsAssign = a.name.includes("Assign New Classroom teacher");
        const bNeedsAssign = b.name.includes("Assign New Classroom teacher");

        return bNeedsAssign - aNeedsAssign;
      });

      setClasses(sortedClasses);

      if (sortedClasses.length > 0) {
        setMostRecentClassId(sortedClasses[0].id);
      }

      // Handle school data from the new API
      if (schoolDataResponse.countryId) {
        setRollNumber(schoolDataResponse.rollnumber);
        setCountryId(schoolDataResponse.countryId);

        const gradesResponse = await getCountryGrades(schoolDataResponse.countryId);
        setGradeLevelOptions([gradesResponse]);
      } else {
        console.warn("School data not found:", schoolDataResponse.message);
        setRollNumber(null);
        setCountryId(null);
      }

    } catch (error) {
      console.error("Error fetching school data:", error);
      setRollNumber(null);
      setCountryId(null);
    } finally {
      setLoading(false);
    }
  };

  if (schoolid) {
    fetchSchoolData();
  }
}, [schoolid]);

  useEffect(() => {
    const fetchTrialistDetails = async () => {
      try {
        const response = await getTrialistsBySchool(schoolid);
        if (response.length > 0) {
          const details = response[0];
          setTrialistDetails(details);

          const currentDate = new Date();
          const endDate = new Date(details.end_date);
          const timeDiff = endDate - currentDate;
          setFreeDaysLeft(
            Math.max(0, Math.ceil(timeDiff / (1000 * 60 * 60 * 24)))
          );
        }
      } catch (error) {
        console.error("Error fetching trialist details:", error);
      }
    };

    if (schoolid) {
      fetchTrialistDetails();
    }
  }, [schoolid]);

  // Fetch the converted message from the database
  useEffect(() => {
    if (countryId) {
      getMessageByMessageId(countryId, 'converted_message')
        .then((msg) => {
          if (msg) {
            setConvertedMessage(msg);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch converted message:', error);
        });

      getMessageByMessageId(countryId, 'converted_message_g1')
        .then((msg) => {
          if (msg) {
            setConvertedMessageG1(msg);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch converted message for Grade 1:', error);
        });

      getMessageByMessageId(countryId, 'converted_message_g3')
        .then((msg) => {
          if (msg) {
            setConvertedMessageG3(msg);
          }    
        })
        .catch((error) => {
          console.error('Failed to fetch converted message for Grade 3:', error);
        });
      // fetch claim banner template (optional)
      getMessageByMessageId(countryId, 'class_claim_message')
        .then((msg) => {
          if (msg) setClaimBannerMessage(msg);
        })
        .catch((err) => {
          console.error('Failed to fetch claim banner message:', err);
        });
    }
  }, [countryId]);

  useEffect(() => {
    const fetchLatestPurchase = async () => {
      if (!schoolid) return;

      try {
        const purchaseResponse = await getPurchasesBySchool(schoolid, {
          order: "next_payment_date DESC",
          limit: 1,
        });

        setLatestPurchase(
          Array.isArray(purchaseResponse) && purchaseResponse.length > 0
            ? purchaseResponse[0]
            : null
        );
      } catch (error) {
        console.error("Error fetching purchase data:", error);
        setLatestPurchase(null);
      }
    };

    fetchLatestPurchase();
  }, [schoolid]);

  const sortedFilteredClasses = useMemo(() => {
    return Array.from(new Set(filteredClasses)).sort((a, b) => {
      // Place classes that need claiming after the teacher's other classes
      const aNeedsClaiming = classesNeedingClaiming.includes(a.id);
      const bNeedsClaiming = classesNeedingClaiming.includes(b.id);

      if (aNeedsClaiming && !bNeedsClaiming) return 1;
      if (!aNeedsClaiming && bNeedsClaiming) return -1;

      // Second priority: Sort by date_created (most recent first)
      const aDateCreated = new Date(a.date_created);
      const bDateCreated = new Date(b.date_created);

      if (aDateCreated > bDateCreated) return -1;
      if (aDateCreated < bDateCreated) return 1;

      // Third priority: filteredClassObjects (legacy logic)
      const aInFiltered = filteredClassObjects.some((obj) => obj.id === a.id);
      const bInFiltered = filteredClassObjects.some((obj) => obj.id === b.id);
      if (aInFiltered && !bInFiltered) return -1;
      if (!aInFiltered && bInFiltered) return 1;

      return 0;
    });
  }, [filteredClasses, classesNeedingClaiming, filteredClassObjects]);

  // Who can see claimables (SI, 1st Class, or higher-tier)
  const canSeeClaimables = useMemo(() => {
    return isSeniorInfantTeacher || isFirstClassTeacher || hasHigherTieredAccess;
  }, [isSeniorInfantTeacher, isFirstClassTeacher, hasHigherTieredAccess]);

  // Merge "my classes" + claimables for display (claimables last, no dups)
  const displayCards = useMemo(() => {
    const mine = sortedFilteredClasses;
    const canUseClaimables =
      legacyClaimablesAllowed &&
      (canSeeClaimables || Boolean(validatedClassroomHandoverSelection));
    if (!canUseClaimables) return mine;

    const mineIds = new Set(mine.map((c) => c.id));
    const open = filterClaimablesBySelection(
      claimableClasses,
      validatedClassroomHandoverSelection
    ).filter((c) => !mineIds.has(c.id));
    return [...mine, ...open];
  }, [sortedFilteredClasses, claimableClasses, canSeeClaimables, legacyClaimablesAllowed, validatedClassroomHandoverSelection]);

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };

    window.addEventListener("resize", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  const handleTrialEndedClick = useCallback(
    () => setShowTrialEndedModal(true),
    []
  );
  const handleCloseTrialEndedModal = useCallback(
    () => setShowTrialEndedModal(false),
    []
  );

  const createCardDisabled = isAccessRestricted;

  useEffect(() => {
    const fetchClassesNeedingClaiming = async () => {
      if (schoolid) {
        try {
          const collectedClaimables = [];
          const claimableAcademicYear =
            validatedClassroomHandoverSelection?.academicYear ||
            currentAcademicYear.storage;
          
          // Use the server-selected progression for handover; retain role-derived
          // numeric grades only for the legacy claimable flow.
          const progressionsToCheck = validatedClassroomHandoverSelection
            ? [validatedClassroomHandoverSelection.progressionKey]
            : [
              ...(isSeniorInfantTeacher ? [2] : []),
              ...(isFirstClassTeacher ? [3] : []),
              ...(hasHigherTieredAccess ? [2, 3] : []),
            ];

          // Remove duplicates
          const uniqueProgressions = [...new Set(progressionsToCheck)];

          const promises = uniqueProgressions.map(async (progression) => {
            try {
              const gradeLevel = typeof progression === "number"
                ? progression
                : validatedClassroomHandoverSelection?.gradeLevel;
              const transitionedClassesResponse = await getAvailableTransitionedClasses(
                schoolid,
                progression,
                claimableAcademicYear
              );

              if (transitionedClassesResponse.success && transitionedClassesResponse.classes) {
                const mapped = transitionedClassesResponse.classes.map(cls => ({
                  ...cls,
                  claimable: true,
                  needsClaiming: true,
                  academicYear: claimableAcademicYear,
                  progressionKey: typeof progression === "string"
                    ? progression
                    : `grade${progression}`,
                  ...(gradeLevel !== null && gradeLevel !== undefined
                    ? { [`grade_level${gradeLevel}`]: true }
                    : {}),
                }));
                return mapped;
              }
              
              return [];
            } catch (error) {
              console.error(`Error fetching transitioned classes for progression ${progression}:`, error);
              return [];
            }
          });

          const results = await Promise.allSettled(promises);

          results.forEach((result) => {
            if (result.status === 'fulfilled') {
              collectedClaimables.push(...result.value);
            }
          });

          // Deduplicate by id
          const dedupById = Object.values(
            collectedClaimables.reduce((acc, cls) => {
              acc[cls.id] = acc[cls.id] ?? cls;
              return acc;
            }, {})
          );

          setClaimableClasses(dedupById);
          setClassesNeedingClaiming(dedupById.map(c => c.id));

        } catch (error) {
          console.error("Error in fetchClassesNeedingClaiming:", error);
          setClassesNeedingClaiming([]);
          setClaimableClasses([]);
        }
      }
    };

    if (!schoolid) {
      setClassesNeedingClaiming([]);
      setClaimableClasses([]);
      return;
    }

    if (legacyClaimablesAllowed) {
      fetchClassesNeedingClaiming();
    } else {
      setClassesNeedingClaiming([]);
      setClaimableClasses([]);
    }
  }, [schoolid, isSeniorInfantTeacher, isFirstClassTeacher, hasHigherTieredAccess, currentAcademicYear, legacyClaimablesAllowed, validatedClassroomHandoverSelection]);

  // Claim handler for claimable class cards
  // const handleClaim = useCallback(async (cls) => {
  //   try {
  //     setClaimingId(cls.id);

  //     await removeOrReplaceClassOwner(cls.id, teacherid);

  //     // Open rename modal immediately after claim
  //     setSelectedClasses([cls.id]);
  //     setSelectedClassName(cls.name || "");
  //     setSelectedClassGradeLevel(String(cls.grade_level || "2"));
  //     setModalScreen('edit-name');
  //     setShowClassSelectionModal(true);

  //     // Refresh my classes and drop from claimables
  //     const refreshed = await getClasses();
  //     setClasses(refreshed);

  //     setClaimableClasses(prev => prev.filter(c => c.id !== cls.id));
  //     setClassesNeedingClaiming(prev => prev.filter(id => id !== cls.id));
  //   } catch (e) {
  //     console.error("Error claiming class", e);
  //     alert("Sorry, couldn't claim that class. Please try again.");
  //   } finally {
  //     setClaimingId(null);
  //   }
  // }, [teacherid]);

  // Function to handle class name updates
  const handleClassNameUpdate = async () => {
    if (!selectedClassName.trim()) {
      alert("Please enter a class name");
      return;
    }

    setIsUpdatingClassName(true);

    try {
      const selectedClassId = selectedClasses[0];
      await editClass(selectedClassId, selectedClassName, selectedClassGradeLevel);

      setClassNameUpdateSuccess(true);

      // Close modal after success and navigate to class page
      setTimeout(() => {
        setShowClassSelectionModal(false);
        setModalScreen('selection');
        setClassNameUpdateSuccess(false);

        // Find the updated class details before navigating
        getClassroomDashboardClasses(schoolid).then(classesResponse => {
          const updatedClass = classesResponse.find(cls => cls.id === selectedClassId);

          if (updatedClass) {
            navigate('/class/detail', {
              state: {
                classDetails: { classDetails: updatedClass },
                teachers: { teachers },
                name: { name: selectedClassName },
                gradeLevel: { gradeLevel: selectedClassGradeLevel },
                rollNumber: { rollNumber },
                teacherEmail: { teacherEmail },
                schoolid: { schoolid },
                isAccessRestricted: { isAccessRestricted },
                isPilot: { isPilot },
                teacherRoles: { teacherRoles },
                teacherid: { teacherid },
                teacherName: { teacherName },
                fromClassSelection: true
              }
            });
          } else {
            // Refresh classes as a fallback
            const uniqueClasses = Array.from(
              new Set(classesResponse.map((cls) => cls.id))
            ).map((id) => {
              return classesResponse.find((cls) => cls.id === id);
            });

            const sortedClasses = uniqueClasses.sort((a, b) => {
              const aDateCreated = new Date(a.date_created);
              const bDateCreated = new Date(b.date_created);

              if (aDateCreated > bDateCreated) return -1;
              if (aDateCreated < bDateCreated) return 1;

              return 0;
            });

            setClasses(sortedClasses);
          }
        }).catch(error => {
          console.error("Error refreshing classes:", error);
        });
      }, 1500);

    } catch (error) {
      console.error("Error updating class name:", error);
      alert("Error updating class name. Please try again.");
    } finally {
      setIsUpdatingClassName(false);
    }
  };

  // Handle class card click - shows verification modal for claiming classes
  const handleClassCardClick = useCallback((classDetails) => {
    // If this class needs claiming, show verification modal instead of normal navigation
    if (classesNeedingClaiming.includes(classDetails.id) || classDetails.claimable) {
      setSelectedClassForVerification(classDetails);
      setShowStudentVerificationModal(true);
    } else {
      // Normal class navigation logic here (if you have any)
      // This would be your existing class card click behavior
    }
  }, [classesNeedingClaiming]);

  // Navigate to settings page after verification - NO claiming happens here
  const handleNavigateAfterVerification = useCallback(async (classDetails) => {
    try {
      setIsNavigatingAfterVerification(true);

      // Close the verification modal
      setShowStudentVerificationModal(false);
      setSelectedClassForVerification(null);

      // Navigate to the normal class detail page like a regular class card does
      navigate('/class/settings/special', {
        state: {
          classDetails: { classDetails },
          teachers: { teachers },
          name: { name: classDetails.name },
          gradeLevel: { gradeLevel: classDetails.grade_level },
          rollNumber: { rollNumber },
          teacherEmail: { teacherEmail },
          schoolid: { schoolid },
          isAccessRestricted: { isAccessRestricted },
          isPilot: { isPilot },
          teacherRoles: { teacherRoles },
          teacherid: { teacherid },
          teacherName: { teacherName }
        }
      });

    } catch (e) {
      console.error("Error navigating after verification", e);
      alert("Sorry, couldn't navigate to the class. Please try again.");
    } finally {
      setIsNavigatingAfterVerification(false);
    }
  }, [navigate, teachers, rollNumber, teacherEmail, schoolid, isAccessRestricted, teacherRoles, teacherid, name, isPilot]);

  // Add handler to close the verification modal
  const handleCloseStudentVerificationModal = useCallback(() => {
    if (!isNavigatingAfterVerification) {
      setShowStudentVerificationModal(false);
      setSelectedClassForVerification(null);
    }
  }, [isNavigatingAfterVerification]);

  const filteredClaimableClasses = useMemo(() => {
  if (!selectedGradeForBanner || selectedGradeForBanner === 'all') {
    return classesNeedingClaiming;
  }

  // Extract grade number from selectedGradeForBanner (e.g., 'grade1' -> 1)
  const gradeNumber = parseInt(selectedGradeForBanner.replace('grade', ''));
  
  return classesNeedingClaiming.filter(classId => {
    // Find the actual class object
    const classObj = claimableClasses.find(cls => cls.id === classId);
    if (!classObj) return false;
    
    // Check if this class matches the selected grade level
    const gradeKey = `grade_level${gradeNumber}`;
    return classObj[gradeKey] === true;
  });
}, [classesNeedingClaiming, selectedGradeForBanner, claimableClasses]);

// Determine if banner should be shown based on teacher role and selected grade
const shouldShowClaimBanner = useMemo(() => {
  // If no grade is selected or 'all' is selected, don't show role-specific banners
  if (!selectedGradeForBanner || selectedGradeForBanner === 'all') {
    return (isSeniorInfantTeacher || isFirstClassTeacher || hasHigherTieredAccess) && 
           filteredClaimableClasses.length > 0;
  }

  // For specific grade selections, only show to relevant teachers
  if (selectedGradeForBanner === 'grade1') {
    // Junior Infant - no claiming needed, so no banner
    return false;
  } else if (selectedGradeForBanner === 'grade2') {
    // Senior Infant - only show to SI teachers or higher-tier
    return (isSeniorInfantTeacher || hasHigherTieredAccess) && 
           filteredClaimableClasses.length > 0;
  } else if (selectedGradeForBanner === 'grade3') {
    // 1st Class - only show to 1st Class teachers or higher-tier
    return (isFirstClassTeacher || hasHigherTieredAccess) && 
           filteredClaimableClasses.length > 0;
  }

  return false;
}, [
  selectedGradeForBanner, 
  isSeniorInfantTeacher, 
  isFirstClassTeacher, 
  hasHigherTieredAccess, 
  filteredClaimableClasses.length
]);

// Get the appropriate grade name for the banner
const getGradeNameForBanner = () => {
  if (selectedGradeForBanner === 'grade2') {
    return 'Senior Infant';
  } else if (selectedGradeForBanner === 'grade3') {
    return '1st Class';
  }
  return 'Classes';
};


  const bannerConfig = getBannerMessage();

  // Determine which DB message object is selected for the banner 
  const selectedBannerMsg = selectedGradeForBanner === 'grade1'
    ? (convertedMessageG1 || "")
    : selectedGradeForBanner === 'grade3'
      ? (convertedMessageG3 || "")
      : (convertedMessage || "");

  const bannerRoute = selectedBannerMsg?.route || "";
  const bannerHyperlink = selectedBannerMsg?.hyperlink || "";


  console.log(displayCards)

   useEffect(() => {
      console.log('🚀 fetchPilotAndScreeningData useEffect triggered');
      console.log('📍 Current values:', { schoolid, countryId });
  
      const fetchPilotAndScreeningData = async () => {
        if (!schoolid || !countryId) {
          console.log('❌ Missing required values, returning early:', { schoolid, countryId });
          return;
        }
  
        try {
          // Fetch pilot data
          console.log('🔍 Fetching pilot data for schoolid:', schoolid);
          const pilotResponse = await getPilotBySchoolId(schoolid);
          console.log('📊 Pilot data:', pilotResponse);
          setPilotData(pilotResponse);
  
          // Fetch screening window status
          console.log('🔍 Fetching screening window for countryid:', countryId);
  
          let screeningResponse = await getScreeningWindowStatuses({
            countryid: countryId,
            active: true
          });
  
          console.log('📊 Screening window data (original):', screeningResponse);
  
          // If no results, fallback to default
          if (!screeningResponse || screeningResponse.length === 0) {
            console.log('⚠️ Using default countryId:', DEFAULT_COUNTRY_ID);
            screeningResponse = await getScreeningWindowStatuses({
              countryid: DEFAULT_COUNTRY_ID,
              active: true
            });
            console.log('📊 Screening window data (default):', screeningResponse);
          }
  
          setScreeningWindowData(screeningResponse);
  
        } catch (error) {
          console.error('❌ Error fetching pilot/screening data:', error);
        }
      };
  
      fetchPilotAndScreeningData();
    }, [schoolid, countryId]);

  return (
    <div className={isClassroomHandoverJourney || isDifferentGroupingsJourneyRoute ? "classroom-handover-page" : ""}>
      <Container fluid className="vh-100">
        <HeadingBanner
          name={`Welcome ${name}!`}
          isLoggedIn={true}
          cloud={true}
          schoolid={schoolid}
          teacherid={teacherid}
          pilotData={pilotData}
          screeningWindowData={screeningWindowData}
        />
        <Row className="h-100">
          <Suspense fallback={<div>Loading Left Panel...</div>}>
            <LeftPanel className={isClassroomHandoverJourney || isDifferentGroupingsJourneyRoute ? "handover-shell-navigation" : ""} />
          </Suspense>
          <Col
            sm={{ span: 10, offset: 2 }}
            data-testid="class-admin-main"
            className={`blue-background ${isClassroomHandoverJourney || isDifferentGroupingsJourneyRoute ? "handover-shell-content" : ""}`}
          >
            {loading || seniorInfantCheckLoading || classroomHandover.status === "loading" ||
            (!isClassroomHandoverReadFailure && (legacyClassroomViewRequested || isClassroomSetupEntryVisible || isDifferentGroupingsJourneyRoute) && classroomHandover.differentGroupingsTaskStatus === "loading") ? (
              <div className="spinner-container">
                <div className="big-ball-spinner"></div>
              </div>
            ) : isDifferentGroupingsJourneyRoute && isClassroomHandoverReadFailure ? (
              <section className="classroom-setup-card classroom-entry-message" role="alert">
                <p>We couldn’t check classroom setup. Try again before continuing.</p>
                <button type="button" onClick={() => classroomHandover.load().catch(() => {})}>Try again</button>
              </section>
            ) : (isClassroomSetupEntryVisible || isDifferentGroupingsJourneyRoute) && classroomHandover.status !== "not_found" && classroomHandover.differentGroupingsTaskStatus === "error" ? (
              <section className="classroom-setup-card classroom-entry-message" role="alert">
                <p>We couldn’t check classroom setup progress. Refresh before choosing a journey.</p>
                <button type="button" onClick={() => classroomHandover.load().catch(() => {})}>Try again</button>
              </section>
            ) : isDifferentGroupingsJourneyRoute && classroomHandover.differentGroupingsAvailability === "loading" ? (
              <div className="spinner-container">
                <div className="big-ball-spinner"></div>
              </div>
            ) : isDifferentGroupingsJourneyRoute && classroomHandover.differentGroupingsEnabled ? (
              <Suspense fallback={<div className="spinner-container"><div className="big-ball-spinner"></div></div>}>
                {isDifferentGroupingsNominationRoute ? (
                  <DifferentGroupingsNomination
                    schoolId={schoolid}
                    academicYear={new URLSearchParams(location.search).get("academicYear") || currentAcademicYear.storage}
                  />
                ) : (
                  <DifferentGroupingsPlaceholder
                    schoolId={schoolid}
                    academicYear={classroomHandover.entry?.academicYear || currentAcademicYear.storage}
                    progressionPoints={classroomHandover.entry?.progressionPoints}
                  />
                )}
              </Suspense>
            ) : isDifferentGroupingsJourneyRoute ? (
              <Navigate
                replace
                to={differentGroupingsFallback}
                state={{ returnFocus: "classroom-setup-heading" }}
              />
            ) : isClassroomSetupEntryVisible ? (
              <Suspense fallback={<div className="spinner-container"><div className="big-ball-spinner"></div></div>}>
                <ClassroomSetupEntry
                  schoolId={schoolid}
                  entry={classroomHandover.entry}
                  status={classroomHandover.status}
                  onContinue={classroomHandover.submitDecision}
                  onRetryDecision={classroomHandover.retryDecision}
                  onRefresh={classroomHandover.load}
                  differentGroupingsEnabled={classroomHandover.differentGroupingsEnabled}
                  normalProgressionPoint={normalProgressionPoint}
                  onNormalClassroomSelect={() => {
                    if (normalProgressionPoint) {
                      navigate(destinationWithSelection(normalProgressionPoint.destination));
                    }
                  }}
                  onCompletedProgressionSelect={(point) => {
                    navigate(destinationWithSelection({
                      ...point.destination,
                      url: "/classadmindashboard?view=classes",
                      academicYear: classroomHandover.entry.academicYear,
                      progressionKey: point.key,
                    }));
                  }}

                />
              </Suspense>
            ) : validatedClassroomHandoverSelection ? (
              <Suspense fallback={<div className="spinner-container"><div className="big-ball-spinner"></div></div>}>
                <HandoverMyClasses
                  selection={validatedClassroomHandoverSelection}
                  classes={classes}
                  progressedClasses={progressedClassrooms.classes}
                  progressedStatus={progressedClassrooms.status}
                  onRetryProgressed={progressedClassrooms.load}
                  teacherId={teacherid}
                  schoolId={schoolid}
                  classCardProps={{
                    teachers,
                    rollNumber,
                    teacherEmail,
                    schoolid,
                    filteredClassObjects,
                    isAccessRestricted,
                    isPilot,
                    teacherRoles,
                    teacherid,
                    teacherName: name,
                  }}
                  createClassProps={{
                    schoolid,
                    teacherid,
                    name,
                    classes,
                    rollNumber,
                    teacherRoles,
                    countryId,
                    teachers,
                  }}
                  progressionPoints={classroomHandover.entry?.progressionPoints || []}
                  onSelectProgression={(progressionKey) => {
                    const point = classroomHandover.entry?.progressionPoints?.find(
                      (candidate) => candidate.key === progressionKey
                    );
                    if (point?.enabled) {
                      navigate(destinationWithSelection(point.destination));
                    }
                  }}
                />
              </Suspense>
            ) : (
              <>
                {isClassroomHandoverReadFailure && (
                  <section
                    className="classroom-setup-card classroom-entry-message"
                    role="alert"
                    aria-labelledby="handover-read-error-title"
                  >
                    <h1 id="handover-read-error-title">
                      {classroomHandover.status === "read_forbidden"
                        ? "Classroom setup is not available to this account"
                        : "We couldn’t check classroom setup"}
                    </h1>
                    <p>
                      Your existing classes are shown below. Classroom handover options
                      will remain unavailable until this check succeeds.
                    </p>
                    <button type="button" onClick={() => classroomHandover.load().catch(() => {})}>
                      Try again
                    </button>
                  </section>
                )}
                <Row className="row-position">
                  <Suspense
                    fallback={<div>Loading Free Trial Banner...</div>}
                  >
                    <FreeTrialBanner
                      schoolid={schoolid}
                      teacherName={name}
                      freeDaysLeft={freeDaysLeft}
                      trialistDetails={trialistDetails}
                      pilotData={pilotData}
                    />
                  </Suspense>
                  {classes.length > 0 && (
                    <Suspense fallback={<div>Loading Class Filter...</div>}>
                      <ClassFilter
                        classes={classes}
                        setFilteredClasses={setFilteredClasses}
                        gradeLevelOptions={gradeLevelOptions}
                        teacherRoles={teacherRoles}
                        schoolid={schoolid}
                        handoverSelection={legacyClassroomSelection}
                        setSelectedGradeForBanner={setSelectedGradeForBanner}
                      />
                    </Suspense>
                  )}

                  {/* Classes to be Claimed Message */}
                  {shouldShowClaimBanner && claimBannerMessage && claimBannerMessage.active && claimBannerMessage.text && claimBannerMessage.text.trim() !== "" && (
                    <Row className="my-3">
                      <Col>
                        {(() => {
                          const tpl = claimBannerMessage.text
                            .replace(/\{count\}/g, String(filteredClaimableClasses.length))
                            .replace(/\{gradeName\}/g, getGradeNameForBanner())
                            .replace(/\r?\n/g, '<br/>');

                          const style = {
                            backgroundColor: '#ffd700',
                            border: '3px solid #ffb300',
                            borderRadius: '12px',
                            padding: '20px',
                            textAlign: 'center',
                            color: '#333333',
                            fontSize: '20px',
                            fontWeight: '700',
                            boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)',
                            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.1)'
                          };

                          if (claimBannerMessage.route && claimBannerMessage.route.trim() !== "") {
                            return (
                              <div onClick={() => navigate(claimBannerMessage.route)} role="button" style={{ ...style, cursor: 'pointer' }}>
                                <span dangerouslySetInnerHTML={{ __html: tpl }} />
                              </div>
                            );
                          }

                          if (claimBannerMessage.hyperlink && claimBannerMessage.hyperlink.trim() !== "") {
                            return (
                              <a href={claimBannerMessage.hyperlink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                <div style={{ ...style, cursor: 'pointer' }}>
                                  <span dangerouslySetInnerHTML={{ __html: tpl }} />
                                </div>
                              </a>
                            );
                          }

                          return (
                            <div style={style}>
                              <span dangerouslySetInnerHTML={{ __html: tpl }} />
                            </div>
                          );
                        })()}
                      </Col>
                    </Row>
                  )}

                  {/* Dynamic info message about class creation */}
                  {countryId === DEFAULT_COUNTRY_ID && (convertedMessage?.active || convertedMessageG1?.active || convertedMessageG3?.active) && bannerConfig.message && bannerConfig.message.trim() !== "" && (
                    <Row className="my-4">
                      <Col>
                        {bannerRoute ? (
                          <div onClick={() => navigate(bannerRoute)} role="button" style={{ ...bannerConfig.style, cursor: 'pointer' }}>
                            <div dangerouslySetInnerHTML={{ __html: bannerConfig.message }} />
                          </div>
                        ) : bannerHyperlink ? (
                          <a href={bannerHyperlink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <div style={{ ...bannerConfig.style, cursor: 'pointer' }}>
                              <div dangerouslySetInnerHTML={{ __html: bannerConfig.message }} />
                            </div>
                          </a>
                        ) : (
                          <div style={bannerConfig.style}>
                            <div dangerouslySetInnerHTML={{ __html: bannerConfig.message }} />
                          </div>
                        )}
                      </Col>
                    </Row>
                  )}

                  <Row>
                    {createCardDisabled ? (
                      <div
                        className="disabled-card-wrapper"
                        onClick={handleTrialEndedClick}
                      >
                        <Suspense
                          fallback={<div>Loading Create Class Button...</div>}
                        >
                          <CreateClassCard
                            schoolid={schoolid}
                            teacherid={teacherid}
                            name={name}
                            classes={classes}
                            rollNumber={rollNumber}
                            teacherRoles={teacherRoles}
                            countryId={countryId}
                            teachers={teachers}
                          />
                        </Suspense>
                        <Suspense
                          fallback={
                            <div>Loading Create Trial Class button..</div>
                          }
                        >
                          <CreateTrialClassCard
                            schoolid={schoolid}
                            teacherid={teacherid}
                            name={name}
                            classes={classes}
                            teacherRoles={teacherRoles}
                            countryId={countryId}
                          />
                        </Suspense>
                        {teacherRoles?.some((role) =>
                          ["SENCO", "SET"].includes(role)
                        ) && (
                          <Suspense
                            fallback={
                              <div>Loading Create SET Class Button...</div>
                            }
                          >
                            <CreateSETClassCard
                              schoolid={schoolid}
                              teacherid={teacherid}
                              name={name}
                              classes={classes}
                              teacherRoles={teacherRoles}
                              countryId={countryId}
                            />
                          </Suspense>
                        )}
                      </div>
                    ) : (
                      <>
                        <Suspense
                          fallback={<div>Loading Create Class Button...</div>}
                        >
                          <CreateClassCard
                            schoolid={schoolid}
                            teacherid={teacherid}
                            name={name}
                            classes={classes}
                            rollNumber={rollNumber}
                            teacherRoles={teacherRoles}
                            countryId={countryId}
                            teachers={teachers}
                          />
                        </Suspense>
                        <Suspense
                          fallback={
                            <div>Loading Create Trial Class Button...</div>
                          }
                        >
                          <CreateTrialClassCard
                            schoolid={schoolid}
                            teacherid={teacherid}
                            name={name}
                            classes={classes}
                            teacherRoles={teacherRoles}
                            countryId={countryId}
                          />
                        </Suspense>
                        {teacherRoles?.some((role) =>
                          ["SENCO", "SET"].includes(role)
                        ) && (
                          <Suspense
                            fallback={
                              <div>Loading Create SET Class Button...</div>
                            }
                          >
                            <CreateSETClassCard
                              schoolid={schoolid}
                              teacherid={teacherid}
                              name={name}
                              classes={classes}
                              teacherRoles={teacherRoles}
                              countryId={countryId}
                            />
                          </Suspense>
                        )}
                      </>
                    )}
                  </Row>
                </Row>

                <Row className="class-row-position">
                  {displayCards.length === 0 ? (
                    <Col>
                      <Card className="text-center shadow-sm">
                        <Card.Body>
                          <Card.Title>No classes yet</Card.Title>
                          <Card.Text>
                            Select <strong>Create Class</strong> to make a new classroom. If you are
                            expecting classes that are not visible, please contact us at{" "}
                            <a href="mailto:info@alpaca-assessment.com">info@alpaca-assessment.com</a> with the class details.
                          </Card.Text>
                        </Card.Body>
                      </Card>
                    </Col>
                  ) : (
                    displayCards.map((item) => (
                      <Suspense key={item.id} fallback={<div>Loading Class Cards...</div>}>
                        <ClassCard
                          teachers={teachers}
                          classDetails={item}
                          rollNumber={rollNumber}
                          teacherEmail={teacherEmail}
                          schoolid={schoolid}
                          filteredClassObjects={filteredClassObjects}
                          isAccessRestricted={isAccessRestricted}
                          isPilot={isPilot}
                          teacherRoles={teacherRoles}
                          teacherid={teacherid}
                          teacherName={name}
                          needsClaiming={classesNeedingClaiming.includes(item.id) || item.claimable}
                          onCardClick={handleClassCardClick}
                        />
                      </Suspense>
                    ))
                  )}
                </Row>
              </>
            )}
          </Col>
        </Row>
      </Container>

      {isPortrait && classroomHandover.status !== "loading" && !isClassroomHandoverJourney && (
        <Suspense fallback={null}>
          <OrientationPrompt />
        </Suspense>
      )}

      {/* Trial Ended Modal */}
      <Modal show={showTrialEndedModal} onHide={handleCloseTrialEndedModal}>
        <Modal.Header closeButton>
          <Modal.Title>Trial Ended</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Your trial period has ended. To continue enjoying ALPACA's full
          features, please proceed with a purchase.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseTrialEndedModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Class Name Update Modal */}
      <Modal
        show={showClassSelectionModal}
        onHide={() => {}}
        backdrop="static"
        keyboard={false}
        centered
        size="lg"
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>Rename Your Class</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="edit-class-name-container">
            <p className="mb-4">
              <strong>Great! Now you can rename your class as it is in Aladdin for the new school year.</strong>
            </p>

            <div className="form-group mb-4">
              <label htmlFor="className" className="form-label">Class Name:</label>
              <input
                type="text"
                className="form-control"
                id="className"
                value={selectedClassName}
                onChange={(e) => setSelectedClassName(e.target.value)}
                placeholder="Enter a name for your class"
                disabled={isUpdatingClassName || classNameUpdateSuccess}
              />
              <small className="text-muted">
                This is the name that will appear on your class dashboard.
              </small>
            </div>

            {isUpdatingClassName && (
              <div className="text-center mt-3">
                <p className="mt-2">Updating class name...</p>
              </div>
            )}

            {classNameUpdateSuccess && (
              <div className="text-center mt-3">
                <p className="text-success">
                  <strong>Class name updated successfully! Redirecting to your class...</strong>
                </p>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end">
          <button
            className={`confirm-button ${isUpdatingClassName ? 'loading' : ''} ${classNameUpdateSuccess ? 'success' : ''}`}
            onClick={handleClassNameUpdate}
            disabled={isUpdatingClassName || !selectedClassName.trim() || classNameUpdateSuccess}
          >
            {classNameUpdateSuccess ? 'Name Updated!' :
              isUpdatingClassName ? 'Updating...' :
                "Save Class Name"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Student Verification Modal */}
      <Suspense fallback={null}>
        <StudentVerificationModal
          show={showStudentVerificationModal}
          onHide={handleCloseStudentVerificationModal}
          onConfirm={handleNavigateAfterVerification}
          classDetails={selectedClassForVerification}
          isConfirming={isNavigatingAfterVerification}
        />
      </Suspense>
    </div>
  );
}

export default ClassAdminDashboard;


// import React, {
//   useEffect,
//   useState,
//   useMemo,
//   useCallback,
//   Suspense,
// } from "react";
// import { Container, Row, Col, Modal, Button, Card } from "react-bootstrap";
// import { useLocation, useNavigate } from "react-router-dom";
// import HeadingBanner from "../components/HeadingBanner";
// import {
//   whoami,
//   getClasses,
//   getTeachers,
//   getSchools,
//   getCountryGrades,
//   shareClass,
//   getTrialistsBySchool,
//   // getClassConversionMethods,
//   getAvailableTransitionedClasses,
//   removeOrReplaceClassOwner,
//   editClass,
//   checkTeacherHasClaimedClass
// } from "../services/APIClient";
// import "../components/ClassCard.css";

// // Lazy Loaded Components
// const ClassCard = React.lazy(() => import("../components/ClassCard"));
// const CreateClassCard = React.lazy(() =>
//   import("../components/CreateClassCard")
// );
// const CreateTrialClassCard = React.lazy(() =>
//   import("../components/CreateTrialClassCard")
// );
// const CreateSETClassCard = React.lazy(() =>
//   import("../components/CreateSETClassCard")
// );
// const LeftPanel = React.lazy(() => import("../components/LeftPanel"));
// const ClassFilter = React.lazy(() => import("../components/ClassFilter"));
// const FreeTrialBanner = React.lazy(() =>
//   import("../components/FreeTrialBanner")
// );
// const OrientationPrompt = React.lazy(() =>
//   import("../components/OrientationPrompt")
// );

// const ReceivedListModal = React.lazy(() => import("../components/ReceivedListModal"));

// function ClassAdminDashboard() {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const [schoolid, setSchoolid] = useState(null);
//   const [teacherid, setTeacherid] = useState(null);
//   const [name, setName] = useState("");
//   const [teachers, setTeachers] = useState(null);
//   const [rollNumber, setRollNumber] = useState(null);
//   const [teacherEmail, setTeacherEmail] = useState(null);
//   const [classes, setClasses] = useState([]);
//   const [filteredClasses, setFilteredClasses] = useState([]);
//   const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
//   const [filteredClassObjects, setFilteredClassObjects] = useState([]);
//   const [mostRecentClassId, setMostRecentClassId] = useState(null);
//   const [teacherRoles, setTeacherRoles] = useState(null);
//   const [countryId, setCountryId] = useState(null);
//   const [trialistDetails, setTrialistDetails] = useState(null);
//   const [freeDaysLeft, setFreeDaysLeft] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [showTrialEndedModal, setShowTrialEndedModal] = useState(false);

//   // State for class selection modal
//   const [showClassSelectionModal, setShowClassSelectionModal] = useState(false);
//   const [selectedClasses, setSelectedClasses] = useState([]);
//   const [availableClassesForSelection, setAvailableClassesForSelection] = useState([]);
//   const [classSelectionSubmitting, setClassSelectionSubmitting] = useState(false);
//   const [classSelectionSuccess, setClassSelectionSuccess] = useState(false);

//   // New state for class name editing screen
//   const [modalScreen, setModalScreen] = useState('selection'); // 'selection', 'edit-name'
//   const [selectedClassName, setSelectedClassName] = useState('');
//   const [isUpdatingClassName, setIsUpdatingClassName] = useState(false);
//   const [classNameUpdateSuccess, setClassNameUpdateSuccess] = useState(false);
//   const [selectedClassGradeLevel, setSelectedClassGradeLevel] = useState(null);

//   // Updated state for conversion method tracking
//   const [isSeniorInfantTeacher, setIsSeniorInfantTeacher] = useState(false);
//   const [conversionMethodType, setConversionMethodType] = useState(null); // 'direct', 'mix_reassign', 'mix_reassign_received', null
//   const [hasClaimedClass, setHasClaimedClass] = useState(false);
//   const [showSeniorInfantRestrictionModal, setShowSeniorInfantRestrictionModal] = useState(false);
//   const [seniorInfantCheckLoading, setSeniorInfantCheckLoading] = useState(false);

//   const [showReceivedListModal, setShowReceivedListModal] = useState(false);
//   const [hasAvailableClassesToClaim, setHasAvailableClassesToClaim] = useState(null);

//   const [classesNeedingClaiming, setClassesNeedingClaiming] = useState([]);


//   const { expired = false, purchased = false, purchased_2425 = false, purchased_2526 = false } = trialistDetails || {};

//   console.log(teacherid, "TEACHER ID IN CLASS ADMIN DASHBOARD");
//   console.log(name, "NAME IN CLASS ADMIN DASHBOARD");

//   let teacherName = name;

//   const [isPortrait, setIsPortrait] = useState(
//     window.matchMedia("(orientation: portrait)").matches
//   );

//   // Higher-tier override roles
//   const higherTieredAccess = ["senco", "set", "special educator/set", "principal", "secretary"];

//   // Does the current teacher have any higher-tier role? (case-insensitive, substring-friendly)
//   const hasHigherTieredAccess = useMemo(() => {
//     const roles = (teacherRoles ?? []).map((r) => String(r).toLowerCase());
//     return roles.some((r) => higherTieredAccess.some((h) => r.includes(h)));
//   }, [teacherRoles]);

//   // Single source of truth for whether Senior Infant restrictions should apply
//   const siRestrictionApplies = isSeniorInfantTeacher && !hasHigherTieredAccess;

//   // Helper function to calculate academic year
//   const calculateAcademicYear = (date) => {
//     const year = date.getFullYear();
//     const month = date.getMonth() + 1;

//     let startYear;
//     let endYear;

//     if (month >= 8) {
//       startYear = year;
//       endYear = year + 1;
//     } else {
//       startYear = year - 1;
//       endYear = year;
//     }

//     const startYearShort = startYear.toString().slice(-2);
//     const endYearShort = endYear.toString().slice(-2);

//     return `${startYearShort}-${endYearShort}`;
//   };

//   useEffect(() => {
//     const initialize = async () => {
//       try {
//         const response = await whoami();
//         const { schoolid, id, name, additionalRoles, email } =
//           response.data.stakeholder;

//         setSchoolid(schoolid);
//         setTeacherid(id);
//         setName(name);
//         setTeacherEmail(email);
//         setTeacherRoles(additionalRoles);
//       } catch (error) {
//         console.error("Error during initialization:", error);
//       }
//     };

//     initialize();
//   }, []);

//   // Updated effect to check Senior Infant teacher restrictions with new logic
//   useEffect(() => {
//     const checkSeniorInfantRestrictions = async () => {
//       if (schoolid && teacherid && teacherRoles) {
//         setSeniorInfantCheckLoading(true);

//         try {
//           // Check if user is a Senior Infant teacher (case insensitive)
//           const isSeniorInfant = teacherRoles.some(role =>
//             role.toLowerCase().includes('senior infant')
//           );

//           console.log('🔍 Senior Infant Check:', {
//             teacherRoles,
//             isSeniorInfant,
//             hasHigherTieredAccess
//           });

//           setIsSeniorInfantTeacher(isSeniorInfant);

//           // ⛳️ Override: higher-tier roles remove SI restrictions entirely
//           if (isSeniorInfant && hasHigherTieredAccess) {
//             console.log('✅ Senior Infant with higher-tier access - skipping restrictions');
//             setConversionMethodType(null);
//             setHasClaimedClass(false);
//             return; // Skip the rest of the restriction logic
//           }

//           if (isSeniorInfant) {
//             console.log('📋 Checking Senior Infant teacher restrictions...');
            
//             // Check for conversion methods
//             const filter = {
//               where: {
//                 schoolid: schoolid,
//               }
//             };

//             const currentAcademicYear = calculateAcademicYear(new Date());
//             console.log('📅 Current academic year for restrictions check:', currentAcademicYear);

//             // Check if teacher has already claimed a class
//             const gradeLevel = 2; // Senior Infant grade level
//             console.log('🔍 Checking if teacher has claimed class with params:', {
//               schoolid,
//               teacherid,
//               currentAcademicYear,
//               gradeLevel
//             });

//             try {
//               const claimedStatus = await checkTeacherHasClaimedClass(
//                 schoolid,
//                 teacherid,
//                 currentAcademicYear,
//                 gradeLevel
//               );
              
//               console.log('✅ checkTeacherHasClaimedClass Response:', {
//                 response: claimedStatus,
//                 hasClaimedClass: claimedStatus?.hasClaimedClass,
//                 message: claimedStatus?.message
//               });
              
//               setHasClaimedClass(claimedStatus.hasClaimedClass);
//             } catch (error) {
//               console.error("❌ Error calling checkTeacherHasClaimedClass:", {
//                 error: error.message,
//                 stack: error.stack,
//                 params: { schoolid, teacherid, currentAcademicYear, gradeLevel }
//               });
//               setHasClaimedClass(false);
//             }
//           } else {
//             console.log('ℹ️ Not a Senior Infant teacher - resetting states');
//             // Reset states for non-Senior Infant teachers
//             setConversionMethodType(null);
//             setHasClaimedClass(false);
//           }
//         } catch (error) {
//           console.error("❌ Error in checkSeniorInfantRestrictions:", {
//             error: error.message,
//             stack: error.stack
//           });
//         } finally {
//           setSeniorInfantCheckLoading(false);
//         }
//       }
//     };

//     checkSeniorInfantRestrictions();
//   }, [schoolid, teacherid, teacherRoles, hasHigherTieredAccess]);

//   // Debug modal state
//   useEffect(() => {
//     console.log('🎭 Modal State Debug:', {
//       showClassSelectionModal,
//       availableClassesLength: availableClassesForSelection.length,
//       selectedClassesLength: selectedClasses.length
//     });
//   }, [showClassSelectionModal, availableClassesForSelection, selectedClasses]);

//   // Check if we need to show class selection modal for Senior Infant teachers
//   useEffect(() => {
//     console.log('🔍 CLASS SELECTION MODAL CHECK - useEffect triggered');
//     console.log('📊 Dependencies:', {
//       schoolid,
//       teacherid,
//       teacherRoles,
//       classesLength: classes.length,
//       hasAllRequiredData: !!(schoolid && teacherid && teacherRoles)
//     });

//     const checkForClassSelectionModal = async () => {
//       console.log('🎯 Starting checkForClassSelectionModal function');

//       if (schoolid && teacherid && teacherRoles) {
//         console.log('✅ All required data exists, proceeding with checks');

//         try {
//           // Check if user is a Senior Infant teacher
//           console.log('👩‍🏫 Checking teacher roles:', teacherRoles);

//           const isSeniorInfantTeacherLocal = teacherRoles.some(role =>
//             role.toLowerCase().includes('senior infant')
//           );

//           console.log('🔍 Senior Infant teacher check:', {
//             isSeniorInfantTeacher: isSeniorInfantTeacherLocal,
//             matchingRoles: teacherRoles.filter(role =>
//               role.toLowerCase().includes('senior infant')
//             )
//           });

//           // 🚫 Override: if higher-tier access present, do not show modal
//           if (!isSeniorInfantTeacherLocal || hasHigherTieredAccess) {
//             console.log('❌ Not applicable (not SI or has higher-tier access), exiting');
//             setHasAvailableClassesToClaim(null); // Reset for non-applicable cases
//             return;
//           }

//           console.log('✅ Is Senior Infant teacher, checking conversion method');

//           // Check if they selected "direct" conversion method
//           const filter = {
//             where: {
//               schoolid: schoolid,
//             }
//           };

//           console.log('🔎 Fetching conversion methods with filter:', filter);
//           // const existingMethods = await getClassConversionMethods(filter);
//           // console.log('📋 Existing conversion methods response:', existingMethods);

//           const currentAcademicYear = calculateAcademicYear(new Date());
//           console.log('📅 Current academic year:', currentAcademicYear);

//           // const directMethod = existingMethods.find(
//           //   method => method.academic_year === currentAcademicYear && method.transition_type === 'direct'
//           // );

//           // console.log('🎯 Direct method search result:', { directMethod, foundMethod: !!directMethod });

//           // if (directMethod) {
//           //   console.log('🚀 Direct method found! Checking available classes');

//           //   try {
//           //     // Check if teacher already claimed a class for this academic year and grade
//           //     const gradeLevel = 2; // Senior Infant grade level

//           //     console.log('🔍 Checking if teacher has already claimed a class');

//           //     const claimedStatus = await checkTeacherHasClaimedClass(
//           //       schoolid,
//           //       teacherid,
//           //       currentAcademicYear,
//           //       gradeLevel
//           //     );

//           //     console.log('📋 Claimed status response:', claimedStatus);

//           //     if (claimedStatus.hasClaimedClass) {
//           //       console.log('✅ Teacher already claimed a class:', claimedStatus.message);
//           //       setHasAvailableClassesToClaim(null); // Not relevant when already claimed
//           //       return;
//           //     }

//           //     console.log('📞 Teacher has not claimed a class yet, fetching available classes...');

//           //     // Fetch available transitioned classes for Senior Infant (grade level 2)
//           //     const transitionedClassesResponse = await getAvailableTransitionedClasses(
//           //       schoolid,
//           //       gradeLevel,
//           //       currentAcademicYear
//           //     );

//           //     console.log('📋 Transitioned classes API response:', transitionedClassesResponse);

//           //     if (!transitionedClassesResponse.success) {
//           //       console.log('❌ API call unsuccessful:', transitionedClassesResponse);
//           //       setHasAvailableClassesToClaim(false);
//           //       return;
//           //     }

//           //     // KEY CHANGE: Track whether there are available classes
//           //     const hasClasses = transitionedClassesResponse.totalAvailable > 0;
//           //     setHasAvailableClassesToClaim(hasClasses);

//           //     if (transitionedClassesResponse.totalAvailable === 0) {
//           //       console.log('ℹ️ No available classes to claim for this grade level and academic year');
//           //       // Don't show modal, but allow class creation by setting hasAvailableClassesToClaim to false
//           //       return;
//           //     }

//           //     // Map API response to modal format and show modal
//           //     const classSelectionData = transitionedClassesResponse.classes.map((classItem, index) => ({
//           //       id: classItem.id,
//           //       name: classItem.name,
//           //       description: `Transitioned ${classItem.grade_level} class for ${classItem.academicYear}`,
//           //       studentCount: classItem.studentCount,
//           //       selected: index === 0,
//           //       grade_level: classItem.grade_level,
//           //     }));

//           //     console.log('📋 Setting up modal with real class data:', classSelectionData);
//           //     setAvailableClassesForSelection(classSelectionData);
//           //     setSelectedClasses(classSelectionData.length > 0 ? [classSelectionData[0].id] : []);
//           //     setShowClassSelectionModal(true);
//           //     console.log('✅ Class selection modal should now be visible with real data!');

//           //   } catch (apiError) {
//           //     console.error('❌ Error fetching available transitioned classes or checking claimed status:', apiError);
//           //     setHasAvailableClassesToClaim(false);
//           //   }
//           // } else {
//           //   console.log('❌ No direct conversion method found for current academic year');
//           //   setHasAvailableClassesToClaim(null); // Not relevant without direct method
//           // }
//         } catch (error) {
//           console.error("❌ Error checking for class selection modal:", error);
//           setHasAvailableClassesToClaim(null);
//         }
//       } else {
//         console.log('❌ Missing required data');
//         setHasAvailableClassesToClaim(null);
//       }
//     };

//     checkForClassSelectionModal();
//   }, [schoolid, teacherid, teacherRoles, hasHigherTieredAccess]);

//   useEffect(() => {
//     const fetchSchoolData = async () => {
//       try {
//         const [teachersResponse, classesResponse, schoolsResponse] =
//           await Promise.all([
//             getTeachers(schoolid),
//             getClasses(),
//             getSchools(),
//           ]);

//         setTeachers(teachersResponse);
//         setClasses(classesResponse);

//         const uniqueClasses = Array.from(
//           new Set(classesResponse.map((cls) => cls.id))
//         ).map((id) => {
//           return classesResponse.find((cls) => cls.id === id);
//         });

//         const filtered = uniqueClasses.filter((cls) => {
//           const createdYear = new Date(cls.date_created).getFullYear();
//           const updatedYear = new Date(cls.date_updated).getFullYear();
//           return createdYear === 2023 && updatedYear === 2024;
//         });

//         setFilteredClassObjects(filtered);

//         const sortedClasses = uniqueClasses.sort((a, b) => {
//           const aDateCreated = new Date(a.date_created);
//           const bDateCreated = new Date(b.date_created);

//           if (aDateCreated > bDateCreated) return -1;
//           if (aDateCreated < bDateCreated) return 1;

//           const aNeedsAssign = a.name.includes("Assign New Classroom teacher");
//           const bNeedsAssign = b.name.includes("Assign New Classroom teacher");

//           return bNeedsAssign - aNeedsAssign;
//         });

//         setClasses(sortedClasses);

//         if (sortedClasses.length > 0) {
//           setMostRecentClassId(sortedClasses[0].id);
//         }

//         const matchingSchool = schoolsResponse.data.find(
//           (school) => school.id === schoolid
//         );

//         if (matchingSchool) {
//           setRollNumber(matchingSchool.rollnumber);
//           setCountryId(matchingSchool.countryid);

//           const gradesResponse = await getCountryGrades(
//             matchingSchool.countryid
//           );
//           setGradeLevelOptions([gradesResponse]);
//         }
//       } catch (error) {
//         console.error("Error fetching school data:", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (schoolid) {
//       fetchSchoolData();
//     }
//   }, [schoolid]);

//   useEffect(() => {
//     const fetchTrialistDetails = async () => {
//       try {
//         const response = await getTrialistsBySchool(schoolid);
//         if (response.length > 0) {
//           const details = response[0];
//           setTrialistDetails(details);

//           const currentDate = new Date();
//           const endDate = new Date(details.end_date);
//           const timeDiff = endDate - currentDate;
//           setFreeDaysLeft(
//             Math.max(0, Math.ceil(timeDiff / (1000 * 60 * 60 * 24)))
//           );
//         }
//       } catch (error) {
//         console.error("Error fetching trialist details:", error);
//       }
//     };

//     if (schoolid) {
//       fetchTrialistDetails();
//     }
//   }, [schoolid]);

//   const sortedFilteredClasses = useMemo(() => {
//     return Array.from(new Set(filteredClasses)).sort((a, b) => {
//       // First priority: Classes that need claiming
//       const aNeedsClaiming = classesNeedingClaiming.includes(a.id);
//       const bNeedsClaiming = classesNeedingClaiming.includes(b.id);

//       if (aNeedsClaiming && !bNeedsClaiming) return -1;
//       if (!aNeedsClaiming && bNeedsClaiming) return 1;

//       // Second priority: Sort by date_created (most recent first)
//       const aDateCreated = new Date(a.date_created);
//       const bDateCreated = new Date(b.date_created);

//       if (aDateCreated > bDateCreated) return -1;
//       if (aDateCreated < bDateCreated) return 1;

//       // Third priority: filteredClassObjects (legacy logic)
//       const aInFiltered = filteredClassObjects.some((obj) => obj.id === a.id);
//       const bInFiltered = filteredClassObjects.some((obj) => obj.id === b.id);
//       if (aInFiltered && !bInFiltered) return -1;
//       if (!aInFiltered && bInFiltered) return 1;

//       return 0;
//     });
//   }, [filteredClasses, classesNeedingClaiming, filteredClassObjects]);

//   useEffect(() => {
//     const handleOrientationChange = () => {
//       setIsPortrait(window.innerWidth < window.innerHeight);
//     };

//     window.addEventListener("resize", handleOrientationChange);

//     return () => {
//       window.removeEventListener("resize", handleOrientationChange);
//     };
//   }, []);

//   const handleTrialEndedClick = useCallback(
//     () => setShowTrialEndedModal(true),
//     []
//   );
//   const handleCloseTrialEndedModal = useCallback(
//     () => setShowTrialEndedModal(false),
//     []
//   );

//   // Senior Infant restriction modal handlers
//   // const handleSeniorInfantRestrictionClick = useCallback(
//   //   () => setShowSeniorInfantRestrictionModal(true),
//   //   []
//   // );
//   // const handleCloseSeniorInfantRestrictionModal = useCallback(
//   //   () => setShowSeniorInfantRestrictionModal(false),
//   //   []
//   // );

//   // Class selection modal handlers
//   // const handleClassSelectionChange = (classId) => {
//   //   console.log('🔄 Class selection changed for classId:', classId);
//   //   // For radio button behavior - only one selection allowed
//   //   setSelectedClasses([classId]);
//   //   console.log('📝 Updated selected class:', [classId]);
//   // };

//   // const handleClassSelectionSubmit = async () => {
//   //   console.log('🚀 Submitting class selection:', selectedClasses[0]);
//   //   setClassSelectionSubmitting(true);

//   //   try {
//   //     const selectedClassId = selectedClasses[0];

//   //     // Call the API to update the class owner
//   //     await removeOrReplaceClassOwner(selectedClassId, teacherid);

//   //     console.log('✅ Class owner updated successfully:', {
//   //       classId: selectedClassId,
//   //       newOwnerId: teacherid
//   //     });

//   //     // Find the selected class object to get its name and grade level
//   //     const selectedClass = availableClassesForSelection.find(
//   //       classItem => classItem.id === selectedClassId
//   //     );

//   //     console.log('📋 Selected class details:', selectedClass);

//   //     // Set the class name for editing and store grade level
//   //     if (selectedClass) {
//   //       setSelectedClassName(selectedClass.name);
//   //       setSelectedClassGradeLevel(selectedClass.grade_level || "2");
//   //     }

//   //     // Show success state briefly before proceeding to next screen
//   //     setClassSelectionSuccess(true);

//   //     // Progress to the next screen after a brief delay
//   //     setTimeout(() => {
//   //       setClassSelectionSuccess(false);
//   //       setModalScreen('edit-name');
//   //     }, 1000);

//   //   } catch (error) {
//   //     console.error("❌ Error updating class owner:", error);
//   //     alert("Error assigning class. Please try again.");
//   //   } finally {
//   //     setClassSelectionSubmitting(false);
//   //   }
//   // };

//   // Function to handle class name updates
//   // const handleClassNameUpdate = async () => {
//   //   if (!selectedClassName.trim()) {
//   //     alert("Please enter a class name");
//   //     return;
//   //   }

//   //   setIsUpdatingClassName(true);

//   //   try {
//   //     const selectedClassId = selectedClasses[0];
//   //     await editClass(selectedClassId, selectedClassName, selectedClassGradeLevel);

//   //     console.log('✅ Class name updated successfully:', {
//   //       classId: selectedClassId,
//   //       newName: selectedClassName,
//   //       gradeLevel: selectedClassGradeLevel
//   //     });

//   //     setClassNameUpdateSuccess(true);

//   //     // Close modal after success and navigate to class page
//   //     setTimeout(() => {
//   //       setShowClassSelectionModal(false);
//   //       setModalScreen('selection'); // Reset for next time
//   //       setClassNameUpdateSuccess(false);

//   //       // Find the updated class details before navigating
//   //       getClasses().then(classesResponse => {
//   //         const updatedClass = classesResponse.find(cls => cls.id === selectedClassId);

//   //         if (updatedClass) {
//   //           navigate('/class/detail', {
//   //             state: {
//   //               classDetails: { classDetails: updatedClass },
//   //               teachers: { teachers },
//   //               name: { name: selectedClassName },
//   //               gradeLevel: { gradeLevel: selectedClassGradeLevel },
//   //               rollNumber: { rollNumber },
//   //               teacherEmail: { teacherEmail },
//   //               schoolid: { schoolid },
//   //               expired: { expired },
//   //               purchased: { purchased },
//   //               purchased_2425: { purchased_2425 },
//   //               purchased_2526: { purchased_2526 },
//   //               teacherRoles: { teacherRoles },
//   //               teacherid: { teacherid },
//   //               teacherName: { teacherName },
//   //               fromClassSelection: true
//   //             }
//   //           });
//   //         } else {
//   //           console.error("Updated class not found in refresh data");
//   //           // Refresh classes as a fallback
//   //           const uniqueClasses = Array.from(
//   //             new Set(classesResponse.map((cls) => cls.id))
//   //           ).map((id) => {
//   //             return classesResponse.find((cls) => cls.id === id);
//   //           });

//   //           const sortedClasses = uniqueClasses.sort((a, b) => {
//   //             const aDateCreated = new Date(a.date_created);
//   //             const bDateCreated = new Date(b.date_created);

//   //             if (aDateCreated > bDateCreated) return -1;
//   //             if (aDateCreated < bDateCreated) return 1;

//   //             return 0;
//   //           });

//   //           setClasses(sortedClasses);
//   //         }
//   //       }).catch(error => {
//   //         console.error("Error refreshing classes:", error);
//   //       });
//   //     }, 1500);

//   //   } catch (error) {
//   //     console.error("❌ Error updating class name:", error);
//   //     alert("Error updating class name. Please try again.");
//   //   } finally {
//   //     setIsUpdatingClassName(false);
//   //   }
//   // };

//   // const handleBackFromClassSelection = () => {
//   //   // If in the edit-name screen, go back to selection screen
//   //   if (modalScreen === 'edit-name') {
//   //     setModalScreen('selection');
//   //   } else {
//   //     // Navigate back or close modal
//   //     setShowClassSelectionModal(false);
//   //   }
//   // };

//   // Updated function with Priority 2 (Senior Infant restrictions) removed
//   const shouldDisableCreateCards = () => {
//     // Priority 1: Payment gate (overrides everything else)
//     if (expired && !purchased_2526) {
//       return { disabled: true, reason: 'trial_ended' };
//     }

//     return { disabled: false, reason: null };
//   };

//   const createCardStatus = shouldDisableCreateCards();

//   // const handleReceivedListModalClick = useCallback(
//   //   () => setShowReceivedListModal(true),
//   //   []
//   // );
//   // const handleCloseReceivedListModal = useCallback(
//   //   () => setShowReceivedListModal(false),
//   //   []
//   // );

//  useEffect(() => {
//     const fetchClassesNeedingClaiming = async () => {
//       if (schoolid) {
//         console.log('🎯 Starting fetchClassesNeedingClaiming for schoolid:', schoolid);
        
//         try {
//           const currentAcademicYear = calculateAcademicYear(new Date());
//           console.log('📅 Academic year for classes needing claiming:', currentAcademicYear);
          
//           const allClassIds = [];

//           // Define grade levels that might have transitioned classes
//           const gradeLevelsToCheck = [2];
//           console.log('📊 Grade levels to check for transitioned classes:', gradeLevelsToCheck);

//           // Fetch transitioned classes for each grade level
//           const promises = gradeLevelsToCheck.map(async (gradeLevel) => {
//             console.log(`🔍 Fetching transitioned classes for grade level ${gradeLevel}...`);
//             console.log('📋 API call params:', {
//               schoolid,
//               gradeLevel,
//               currentAcademicYear
//             });

//             try {
//               const transitionedClassesResponse = await getAvailableTransitionedClasses(
//                 schoolid,
//                 gradeLevel,
//                 currentAcademicYear
//               );

//               console.log(`✅ getAvailableTransitionedClasses Response for grade ${gradeLevel}:`, {
//                 fullResponse: transitionedClassesResponse,
//                 success: transitionedClassesResponse?.success,
//                 totalAvailable: transitionedClassesResponse?.totalAvailable,
//                 classesCount: transitionedClassesResponse?.classes?.length,
//                 classes: transitionedClassesResponse?.classes
//               });

//               if (transitionedClassesResponse.success && transitionedClassesResponse.classes) {
//                 const classIds = transitionedClassesResponse.classes.map(cls => cls.id);
//                 console.log(`📝 Extracted class IDs for grade ${gradeLevel}:`, classIds);
//                 return classIds;
//               }
              
//               console.log(`ℹ️ No transitioned classes found for grade ${gradeLevel}`);
//               return [];
//             } catch (error) {
//               console.error(`❌ Error fetching transitioned classes for grade level ${gradeLevel}:`, {
//                 error: error.message,
//                 stack: error.stack,
//                 params: { schoolid, gradeLevel, currentAcademicYear }
//               });
//               return [];
//             }
//           });

//           // Wait for all requests to complete
//           console.log('⏳ Waiting for all grade level requests to complete...');
//           const results = await Promise.allSettled(promises);
          
//           console.log('📊 Promise.allSettled results:', results);

//           // Flatten all successful results
//           results.forEach((result, index) => {
//             if (result.status === 'fulfilled') {
//               console.log(`✅ Grade level ${gradeLevelsToCheck[index]} fulfilled with:`, result.value);
//               allClassIds.push(...result.value);
//             } else {
//               console.log(`❌ Grade level ${gradeLevelsToCheck[index]} rejected:`, result.reason);
//             }
//           });

//           // Remove duplicates and set state
//           const uniqueClassIds = [...new Set(allClassIds)];
//           console.log('🎯 Final results:', {
//             allClassIds,
//             uniqueClassIds,
//             totalClassesNeedingClaiming: uniqueClassIds.length
//           });
          
//           setClassesNeedingClaiming(uniqueClassIds);

//         } catch (error) {
//           console.error("❌ Error in fetchClassesNeedingClaiming:", {
//             error: error.message,
//             stack: error.stack,
//             schoolid
//           });
//           // Set empty array on error to prevent infinite loading states
//           setClassesNeedingClaiming([]);
//         }
//       } else {
//         console.log('⚠️ No schoolid provided to fetchClassesNeedingClaiming');
//       }
//     };

//     // Only fetch if we have schoolid and not already fetched
//     if (schoolid && classesNeedingClaiming.length === 0) {
//       console.log('🚀 Triggering fetchClassesNeedingClaiming...');
//       fetchClassesNeedingClaiming();
//     } else {
//       console.log('⏭️ Skipping fetchClassesNeedingClaiming:', {
//         hasSchoolid: !!schoolid,
//         classesNeedingClaimingLength: classesNeedingClaiming.length
//       });
//     }
//   }, [schoolid, classesNeedingClaiming.length]);

//   console.log(sortedFilteredClasses, "SORTED FILTERED CLASSES IN CLASS ADMIN DASHBOARD");

//   return (
//     <div>
//       <Container fluid className="vh-100">
//         <HeadingBanner
//           name={`Welcome ${name}!`}
//           isLoggedIn={true}
//           cloud={true}
//           teacherid={teacherid}
//           purchased={purchased}
//           purchased_2425={trialistDetails?.purchased_2425}
//           purchased_2526={trialistDetails?.purchased_2526}
//           purchased_2627={trialistDetails?.purchased_2627}
//         />
//         <Row className="h-100">
//           <Suspense fallback={<div>Loading Left Panel...</div>}>
//             <LeftPanel />
//           </Suspense>
//           <Col sm={{ span: 10, offset: 2 }} className="blue-background">
//             {loading || seniorInfantCheckLoading ? (
//               <div className="spinner-container">
//                 <div className="big-ball-spinner"></div>
//               </div>
//             ) : (
//               <>
//                 <Row className="row-position">
//                   {!purchased_2526 ? (
//                     <Suspense
//                       fallback={<div>Loading Free Trial Banner...</div>}
//                     >
//                       <FreeTrialBanner
//                         schoolid={schoolid}
//                         teacherName={name}
//                         freeDaysLeft={freeDaysLeft}
//                         trialistDetails={trialistDetails}
//                       />
//                     </Suspense>
//                   ) : null}
//                   {classes.length > 0 && (
//                     <Suspense fallback={<div>Loading Class Filter...</div>}>
//                       <ClassFilter
//                         classes={classes}
//                         setFilteredClasses={setFilteredClasses}
//                         gradeLevelOptions={gradeLevelOptions}
//                         teacherRoles={teacherRoles}
//                       />
//                     </Suspense>
//                   )}

//                   {/* Senior Infant Classes to be Claimed Message */}
//                   {classesNeedingClaiming.length > 0 && (isSeniorInfantTeacher || hasHigherTieredAccess) && (
//                     <Row className="my-3">
//                       <Col>
//                         <div style={{
//                           backgroundColor: '#ffd700',
//                           border: '3px solid #ffb300',
//                           borderRadius: '12px',
//                           padding: '20px',
//                           textAlign: 'center',
//                           color: '#333333',
//                           fontSize: '20px',
//                           fontWeight: '700',
//                           boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)',
//                           textShadow: '1px 1px 2px rgba(0, 0, 0, 0.1)'
//                         }}>
//                           {classesNeedingClaiming.length} Senior Infant Classes Still need to be Claimed!
//                         </div>
//                       </Col>
//                     </Row>
//                   )}

//                   {/* Info message about class creation */}
//                   <Row className="my-4">
//                     <Col>
//                       <div style={{
//                         backgroundColor: '#cc5182ff',
//                         border: '2px solid #f43782ff',
//                         borderRadius: '12px',
//                         padding: '20px',
//                         textAlign: 'center',
//                         boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
//                         fontSize: '150%',
//                         fontWeight: '600',
//                         color: '#ffffffff',
//                         margin: '0 auto',
//                         maxWidth: '100%'
//                       }}>
//                         Last years Juniors have been converted to Seniors. <br></br>
//                         Please claim or assign these converted classrooms. Junior classrooms can be created as normal.<br></br>
//                         To remove classrooms or teachers please email details to <strong className="email-highlight">info@alpaca-assessment.com</strong>
//                       </div>

//                     </Col>
//                   </Row>

//                   <Row>
//                     {createCardStatus.disabled ? (
//                       <div
//                         className="disabled-card-wrapper"
//                         onClick={() => {
//                           if (createCardStatus.reason === 'trial_ended') {
//                             handleTrialEndedClick();
//                           }
//                           // } else if (createCardStatus.reason === 'mix_reassign_received') {
//                           //   handleReceivedListModalClick();
//                           // } else {
//                           //   handleSeniorInfantRestrictionClick();
//                           // }
//                         }}
//                       >
//                         <Suspense
//                           fallback={<div>Loading Create Class Button...</div>}
//                         >
//                           <CreateClassCard
//                             schoolid={schoolid}
//                             teacherid={teacherid}
//                             name={name}
//                             classes={classes}
//                             rollNumber={rollNumber}
//                             teacherRoles={teacherRoles}
//                             countryId={countryId}
//                             teachers={teachers}
//                           />
//                         </Suspense>
//                         <Suspense
//                           fallback={
//                             <div>Loading Create Trial Class button..</div>
//                           }
//                         >
//                           <CreateTrialClassCard
//                             schoolid={schoolid}
//                             teacherid={teacherid}
//                             name={name}
//                             classes={classes}
//                             teacherRoles={teacherRoles}
//                             countryId={countryId}
//                           />
//                         </Suspense>
//                       </div>
//                     ) : (
//                       <>
//                         <Suspense
//                           fallback={<div>Loading Create Class Button...</div>}
//                         >
//                           <CreateClassCard
//                             schoolid={schoolid}
//                             teacherid={teacherid}
//                             name={name}
//                             classes={classes}
//                             rollNumber={rollNumber}
//                             teacherRoles={teacherRoles}
//                             countryId={countryId}
//                             teachers={teachers}
//                           />
//                         </Suspense>
//                         <Suspense
//                           fallback={
//                             <div>Loading Create Fake Class Button...</div>
//                           }
//                         >
//                           <CreateTrialClassCard
//                             schoolid={schoolid}
//                             teacherid={teacherid}
//                             name={name}
//                             classes={classes}
//                             teacherRoles={teacherRoles}
//                             countryId={countryId}
//                           />
//                         </Suspense>
//                       </>
//                     )}
//                   </Row>
//                 </Row>

//                 <Row className="class-row-position">
//                   {sortedFilteredClasses.length === 0 ? (
//                     <Col>
//                       <Card className="text-center shadow-sm">
//                         <Card.Body>
//                           <Card.Title>No classes yet</Card.Title>
//                           <Card.Text>
//                             Select <strong>Create Class</strong> to make a new classroom. If you are
//                             expecting classes that are not visible, please contact us at{" "}
//                             <a href="mailto:info@alpaca-assessment.com">info@alpaca-assessment.com</a> with the class details.
//                           </Card.Text>
//                         </Card.Body>
//                       </Card>
//                     </Col>
//                   ) : (
//                     sortedFilteredClasses.map((item) => (
//                       <Suspense key={item.id} fallback={<div>Loading Class Cards...</div>}>
//                         <ClassCard
//                           teachers={teachers}
//                           classDetails={item}
//                           rollNumber={rollNumber}
//                           teacherEmail={teacherEmail}
//                           schoolid={schoolid}
//                           filteredClassObjects={filteredClassObjects}
//                           expired={expired}
//                           purchased={purchased}
//                           purchased_2425={purchased_2425}
//                           purchased_2526={purchased_2526}
//                           teacherRoles={teacherRoles}
//                           teacherid={teacherid}
//                           teacherName={name}
//                           needsClaiming={classesNeedingClaiming.includes(item.id)}
//                         />
//                       </Suspense>
//                     ))
//                   )}
//                 </Row>

//               </>
//             )}
//           </Col>
//         </Row>
//       </Container>

//       {isPortrait && (
//         <Suspense fallback={null}>
//           <OrientationPrompt />
//         </Suspense>
//       )}

//       {/* Trial Ended Modal */}
//       <Modal show={showTrialEndedModal} onHide={handleCloseTrialEndedModal}>
//         <Modal.Header closeButton>
//           <Modal.Title>Trial Ended</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           Your trial period has ended. To continue enjoying ALPACA's full
//           features, please proceed with a purchase.
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="primary" onClick={handleCloseTrialEndedModal}>
//             Close
//           </Button>
//         </Modal.Footer>
//       </Modal>

//       {/* Senior Infant Restriction Modal */}
//       {/* <Modal show={showSeniorInfantRestrictionModal} onHide={handleCloseSeniorInfantRestrictionModal}>
//         <Modal.Header closeButton>
//           <Modal.Title>Class Creation Restricted</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <p>
//             <strong>Senior Infant teachers work with transitioned classes from Junior Infants.</strong>
//           </p>
//           <p>
//             You cannot create new classes because:
//           </p>
//           <ul>
//             <li>Your school has not enabled mixed class reassignment for this academic year</li>
//             <li>You have not yet claimed ownership of an existing transitioned class</li>
//           </ul>
//           <p>
//             <strong>What you can do:</strong>
//           </p>
//           <ul>
//             <li>Forward your updated Senior Infant class lists to info@alpaca-assessment.com</li>
//             <li>Look for transitioned Senior Infant classes that you can claim ownership of</li>
//           </ul>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="primary" onClick={handleCloseSeniorInfantRestrictionModal}>
//             Understood
//           </Button>
//         </Modal.Footer>
//       </Modal> */}

//       {/* Class Selection Modal for Senior Infant Teachers */}
//       {/* <Modal
//         show={showClassSelectionModal}
//         onHide={() => { }} // Prevent closing by clicking outside
//         backdrop="static"
//         keyboard={false}
//         centered
//         size="lg"
//       >
//         <Modal.Header closeButton={false}>
//           <Modal.Title>
//             {modalScreen === 'selection' ? 'Select Your Classes' : 'Rename Your Class'}
//           </Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           {modalScreen === 'selection' ? (
//             <>
//               <p className="mb-4">
//                 <strong>Choose which class you will teach for the 2025/26 school year</strong>
//               </p>

//               <div className="class-selection-list">
//                 {availableClassesForSelection.map((classItem) => (
//                   <div key={classItem.id} className="class-selection-item">
//                     <div className="form-check">
//                       <input
//                         className="form-check-input"
//                         type="radio"
//                         name="classSelection"
//                         id={`class-${classItem.id}`}
//                         checked={selectedClasses.includes(classItem.id)}
//                         onChange={() => handleClassSelectionChange(classItem.id)}
//                       />
//                       <label
//                         className="form-check-label"
//                         htmlFor={`class-${classItem.id}`}
//                         style={{ width: '100%' }}
//                       >
//                         <div className="d-flex justify-content-between align-items-center">
//                           <div>
//                             <div className="fw-bold">{classItem.name}</div>
//                             <div className="text-muted small">{classItem.description}</div>
//                           </div>
//                           <div className="text-end">
//                             <span className="badge bg-secondary rounded-pill">
//                               {classItem.studentCount}
//                             </span>
//                           </div>
//                         </div>
//                       </label>
//                     </div>
//                   </div>
//                 ))}
//               </div>

//               {classSelectionSubmitting && (
//                 <div className="text-center mt-3">
//                   <p className="mt-2">Saving your selection...</p>
//                 </div>
//               )}
//             </>
//           ) : (
//             <div className="edit-class-name-container">
//               <p className="mb-4">
//                 <strong>Great! Now you can rename your class as it is in Aladdin for the new school year.</strong>
//               </p>

//               <div className="form-group mb-4">
//                 <label htmlFor="className" className="form-label">Class Name:</label>
//                 <input
//                   type="text"
//                   className="form-control"
//                   id="className"
//                   value={selectedClassName}
//                   onChange={(e) => setSelectedClassName(e.target.value)}
//                   placeholder="Enter a name for your class"
//                   disabled={isUpdatingClassName || classNameUpdateSuccess}
//                 />
//                 <small className="text-muted">
//                   This is the name that will appear on your class dashboard.
//                 </small>
//               </div>

//               {isUpdatingClassName && (
//                 <div className="text-center mt-3">
//                   <p className="mt-2">Updating class name...</p>
//                 </div>
//               )}

//               {classNameUpdateSuccess && (
//                 <div className="text-center mt-3">
//                   <p className="text-success">
//                     <strong>Class name updated successfully! Redirecting to your class...</strong>
//                   </p>
//                 </div>
//               )}
//             </div>
//           )}
//         </Modal.Body>
//         <Modal.Footer className="d-flex justify-content-between">
//           <Button
//             variant="secondary"
//             onClick={modalScreen === 'selection'
//               ? handleBackFromClassSelection
//               : () => setModalScreen('selection')
//             }
//             disabled={classSelectionSubmitting || classSelectionSuccess || isUpdatingClassName || classNameUpdateSuccess}
//           >
//             {modalScreen === 'selection' ? 'Back' : 'Previous Step'}
//           </Button>

//           {modalScreen === 'selection' ? (
//             <button
//               className={`confirm-button ${classSelectionSubmitting ? 'loading' : ''} ${classSelectionSuccess ? 'success' : ''}`}
//               onClick={handleClassSelectionSubmit}
//               disabled={classSelectionSubmitting || selectedClasses.length === 0 || classSelectionSuccess}
//             >
//               {classSelectionSuccess ? 'Class Assigned!' :
//                 classSelectionSubmitting ? 'Saving...' :
//                   "I Teach This Class"}
//             </button>
//           ) : (
//             <button
//               className={`confirm-button ${isUpdatingClassName ? 'loading' : ''} ${classNameUpdateSuccess ? 'success' : ''}`}
//               onClick={handleClassNameUpdate}
//               disabled={isUpdatingClassName || !selectedClassName.trim() || classNameUpdateSuccess}
//             >
//               {classNameUpdateSuccess ? 'Name Updated!' :
//                 isUpdatingClassName ? 'Updating...' :
//                   "Save Class Name"}
//             </button>
//           )}
//         </Modal.Footer>
//       </Modal> */}
// {/* 
//       <ReceivedListModal
//         show={showReceivedListModal}
//         onClose={handleCloseReceivedListModal}
//         submissionData={{
//           date: new Date(), // You might want to get this from your API
//           teacherName: name,
//           academicYear: calculateAcademicYear(new Date())
//         }}
//       /> */}
//     </div>
//   );
// }

// export default ClassAdminDashboard;
